/**
 * @description Hook that manages Stripe SDK integration:
 * - Loads the Stripe SDK
 * - Handles initialization
 * - Generates configuration objects for Stripe Elements
 *
 * @param {AnyGatewayEnv} [env='prod'] - Environment to use for the gateway
 * @param {string} [apiToken=''] - Token used for CB payment service authentication
 * @param {AnyGatewayMode} [mode='live'] - Mode for the Tokenex SDK (live/test)
 * @returns {GatewayHookResponse} Hook response containing loading state and configuration
 */
import {
  loadStripe as stripeLoader,
  StripeElementLocale,
  StripeElementsOptions,
  Stripe,
  StripeElements,
  CreatePaymentMethodData,
} from '@stripe/stripe-js';
import { CardNumberElement } from '@stripe/react-stripe-js';

import {
  FetchStripeGatewayProps,
  StripeCardPaymentResponse,
  StripeFormConfigType,
  StripeGatewayOptions,
} from '../@types';

/**
 * @description Helper to generate Stripe Elements options.
 * @param {Object} params - Parameters for options.
 * @param {StripeGatewayOptions['options']} params.options - Stripe options.
 * @param {string} params.account - Stripe account ID.
 * @param {string} params.type - Gateway type.
 * @returns {StripeElementsOptions} Stripe Elements options object.
 */
const getStripeOptions = ({
  options,
  account,
  type,
}: Pick<StripeGatewayOptions, 'options' | 'account' | 'type'>) => {
  return {
    ...options,
    ...(account &&
      type === 'platform' && {
        onBehalfOf: account,
      }),
  } as StripeElementsOptions;
};

/**
 * @description Loads the Stripe SDK with the provided configuration.
 * @param {StripeGatewayOptions} options - Stripe gateway options.
 * @returns {Promise<Stripe | null>} Promise resolving to Stripe instance or null.
 */
const loadStripeSDK = async ({
  publishableKey,
  configurationOptions,
  betas,
  locale,
  account,
  type,
}: StripeGatewayOptions) => {
  return stripeLoader(publishableKey, {
    ...(configurationOptions && {
      ...configurationOptions,
    }),
    ...(betas && {
      betas: ['elements_enable_deferred_intent_beta_1'],
    }),
    ...(locale && {
      locale: (locale.replace(/[_-].*/g, '') || 'en') as StripeElementLocale,
    }),
    ...(account &&
      type === 'direct' && {
        stripeAccount: account,
      }),
  });
};

/**
 * @description Loads Stripe and prepares configuration for Stripe Elements.
 * @param {FetchStripeGatewayProps} props - Props for fetching Stripe gateway.
 * @returns {Promise<StripeFormConfigType | null>} Stripe form config or null.
 */
export const loadStripe = async ({
  publishableKey,
  account,
  type,
  // TODO: options must be here
}: FetchStripeGatewayProps) => {
  if (!publishableKey) {
    return null;
  }
  const loadStripeParams: StripeGatewayOptions = {
    publishableKey,
    // TODO:
    locale: 'en',
    // TODO: this is for cobadged cards only
    account,
    // TODO: this is for cobadged cards only
    type,
  };
  /* Load the Stripe SDK with the prepared parameters */
  return loadStripeSDK(loadStripeParams).then(stripe => {
    /* Return the Stripe provider and options for Stripe Elements */
    return {
      provider: stripe,
      options: getStripeOptions(loadStripeParams),
    } as StripeFormConfigType;
  });
};

/**
 * @description Creates a Stripe payment method using card details.
 * @param {any} data - Card and billing details.
 * @param {Stripe} stripe - Stripe instance.
 * @param {StripeElements} elements - Stripe Elements instance.
 * @returns {Promise<{paymentMethod?: any, error?: string}>} Result with payment method or error.
 */
export const createStripePaymentMethod = async (
  data: any,
  stripe: Stripe,
  elements: StripeElements,
) => {
  if (!stripe || !elements)
    return {
      error: 'Payment provider is undefined.',
    };

  /* Get the CardNumberElement from Stripe Elements */
  const cardElement = elements.getElement(CardNumberElement);
  if (!cardElement)
    return {
      error: 'Card control is undefined.',
    };

  const params: CreatePaymentMethodData = {
    type: 'card',
    card: cardElement,
    billing_details: {
      name: data.cardholderName, // TODO: this seems incorrect, should be cardholderName
      ...(data.billingDetails?.billingEmail && {
        email: data.billingDetails.billingEmail,
      }),
      address: {
        ...(data.billingDetails?.billingAddress1 && {
          line1: data.billingDetails.billingAddress1,
        }),
        ...(data.billingDetails?.billingAddress2 && {
          line2: data.billingDetails.billingAddress2,
        }),
        ...(data.billingDetails?.billingZip && {
          postal_code: data.billingDetails.billingZip,
        }),
        ...(data.billingDetails?.billingState && {
          state: data.billingDetails.billingState,
        }),
        ...(data.billingDetails?.billingCity && {
          city: data.billingDetails.billingCity,
        }),
        ...(data.billingDetails?.billingCountry && {
          country: data.billingDetails.billingCountry,
        }),
      },
    },
  };

  /* Call Stripe to create a payment method */
  const { error, paymentMethod } = await stripe.createPaymentMethod({
    ...params,
  });

  return {
    paymentMethod,
    error: error?.message,
  };
};

/**
 * @description Creates a Stripe token for the card details.
 * @param {any} data - Card and billing details.
 * @param {Stripe} stripe - Stripe instance.
 * @param {StripeElements} elements - Stripe Elements instance.
 * @returns {Promise<{token?: any, error?: any} | null>} Result with token or error, or null if missing.
 */
export const createStripeToken = async (
  data: any,
  stripe: Stripe,
  elements: StripeElements,
) => {
  if (!stripe || !elements) return null;

  const cardElement = elements.getElement(CardNumberElement);

  if (!cardElement) return null;

  const additionalData = {
    name: data.cardholderName, // TODO: this seems incorrect, should be cardholderName
    ...(data.billingDetails?.billingAddress1 && {
      address_line1: data.billingDetails.billingAddress1,
    }),
    ...(data.billingDetails?.billingAddress2 && {
      address_line2: data.billingDetails.billingAddress2,
    }),
    ...(data.billingDetails?.billingZip && {
      address_zip: data.billingDetails.billingZip,
    }),
    ...(data.billingDetails?.billingState && {
      address_state: data.billingDetails.billingState,
    }),
    ...(data.billingDetails?.billingCity && {
      address_city: data.billingDetails.billingCity,
    }),
    ...(data.billingDetails?.billingCountry && {
      address_country: data.billingDetails.billingCountry,
    }),
  };

  const { token, error } = await stripe.createToken(
    cardElement,
    additionalData,
  );

  return { token, error };
};

/**
 * @description Creates a Stripe payment by tokenizing card and creating a payment method.
 * @param {any} data - Card and billing details.
 * @param {Stripe} stripe - Stripe instance.
 * @param {StripeElements} elements - Stripe Elements instance.
 * @returns {Promise<StripeCardPaymentResponse>} Payment response with token, payment method, or error.
 */
export const createStripePayment = async (
  data: any,
  stripe: Stripe,
  elements: StripeElements,
): Promise<StripeCardPaymentResponse> => {
  const tokenResponse = await createStripeToken(data, stripe, elements);

  if (!tokenResponse?.token?.id || tokenResponse?.error) {
    return {
      error: tokenResponse?.error?.message || 'Failed to tokenize form data.',
    };
  }

  const { token } = tokenResponse;

  const { error, paymentMethod } = await createStripePaymentMethod(
    data,
    stripe,
    elements,
  );

  if (error) {
    return {
      error: error || 'Failed to create payment method.',
    };
  }

  return {
    ...(token?.id && { token: token.id }),
    ...(token.card && { token_card: { ...token.card } }),
    paymentMethod: 'card',
    paymentMethodId: paymentMethod?.id,
  };
};
